import { amountFormatter, pirBoxCardMessages } from 'azshared';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppButtonIcon } from '../button-icon/app-button-icon';
import './barchart.scss';
import { BarChartDatapoint } from './datapoint/datapoint';
import { usePdfGeneration } from '../../hooks/use-pdf-generation';
import { downloadFile } from '../../utils/utils';
import { YearlyPirIndicatorsBean } from 'azshared/build/main/lib/api/generated/objectway-zenith';
import { PirBoxProductSummary } from 'azshared/build/main/lib/model/pirbox/pirbox-product-summary';
import {PirTypesEnumReverse} from 'azshared';

export interface BarChartProps {
  dataSource: YearlyPirIndicatorsBean;
  summary: PirBoxProductSummary;
}

export const BarChart = (props: BarChartProps) => {
  const { dataSource, summary } = props;
  const type = summary?.pirType;
  const title = type === PirTypesEnumReverse.UCITS ? pirBoxCardMessages.ordinaryDepositTitle : pirBoxCardMessages.alternativeDepositTitle;
  const { formatMessage } = useIntl();

  const { generatePdf } = usePdfGeneration();

  const pdfTitle = useMemo(() => {
    return formatMessage(title) + '.pdf';
  }, [formatMessage, title]);

  const getPdf = useCallback(() => {
    generatePdf(document.getElementById('barchart-container'))?.then((v) => {
      if(v) downloadFile(pdfTitle, v);
    });
  }, [generatePdf, pdfTitle]);

  return (
    <div id='barchart-container' className='barchart-container'>
      <div className='barchart'>
        <div className='chart-header'>
          <div className='chart-title'>
            <h4>
              {formatMessage(title)} - {formatMessage(pirBoxCardMessages.investmentPlan)}
            </h4>
          </div>
          <div className='chart-legend'>
            <div className='value-legend-item main'>
              <div className='legend-item-dot'></div>
              <div className='legend-item-label'>{formatMessage(pirBoxCardMessages.netInvestedAmount)}</div>
            </div>
            <div className='value-legend-item secondary'>
              <div className='legend-item-dot'></div>
              <div className='legend-item-label'>{formatMessage(pirBoxCardMessages.investableAmountLabel)}</div>
            </div>
          </div>
        </div>
        <div className='chart-body'>
          <div className='datapoint-container'>
            {dataSource.indicatorMap &&
              Object.entries(dataSource.indicatorMap).map((elem) => (
                <BarChartDatapoint key={elem[0]} data={{ label: elem[0], value: elem[1].amount, max: elem[1].maxInvestmentAmount }} />
              ))}
          </div>
          <div className='chart-buttons' data-html2canvas-ignore='true'>
            <AppButtonIcon className='printer-button' icon='printer' label='Stampa PDF' onClick={getPdf} />
          </div>
        </div>
        <div className='chart-footer'>
          <div className='chart-footer-column'>
            <p className='column-label'>
              {formatMessage(pirBoxCardMessages.investableAmountLabel)}
              {<sup className='disclaimer-suffix'>1</sup>}
            </p>
            <p className='column-amount'>{amountFormatter(summary.totalInvestable)}</p>
          </div>
          <div className='chart-footer-column'>
            <p className='column-label'>
              {formatMessage(pirBoxCardMessages.netInvestedAmount)}
              {<sup className='disclaimer-suffix'>1</sup>}
            </p>
            <p className='column-amount'>{amountFormatter(summary.totalNet)}</p>
          </div>
          <div className='chart-footer-column'>
            <p className='column-label'>
              {formatMessage(pirBoxCardMessages.investableResidueLabel)}
              {<sup className='disclaimer-suffix'>2</sup>}
            </p>
            <p className='column-amount'>{amountFormatter(summary.totalInvestableResidue)}</p>
          </div>
        </div>
        <div className='pirbox-disclaimers'>
          {<p>1: {formatMessage(pirBoxCardMessages.extrordinaryTaxationDisclaimerMessage)}</p>}
          {<p>2: {formatMessage(pirBoxCardMessages.partiteViaggiantiDisclaimerMessage)}</p>}
        </div>
      </div>
    </div>
  );
};
